import { Bell } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { usePendenciasContagem } from '@/lib/api-hooks';
import { cn } from '@/lib/cn';

/**
 * Sprint 16 — badge no header com contador de pendências do usuário logado.
 * Sprint 18 — detecção automática de novas pendências:
 *   - Polling de 5min vem do hook `usePendenciasContagem` (pausa em aba inativa).
 *   - Quando a contagem cresce entre dois fetches, dispara `toast.info` +
 *     animação `animate-pulse` no badge por 3 segundos.
 *   - Não dispara na montagem inicial nem antes do 1º fetch retornar
 *     (`data === undefined` → guarda explícita).
 *
 * Lote D / R-2.12 — agora consome `usePendenciasContagem` (`{ total: number }`)
 * em vez do array completo do `usePendencias({})`. Reduz tráfego do polling.
 */
export function BadgePendencias() {
  const { data: total, isLoading } = usePendenciasContagem();
  const totalNorm = total ?? 0;
  const contagemAnteriorRef = useRef<number | undefined>(undefined);
  const [novasPendencias, setNovasPendencias] = useState(false);

  useEffect(() => {
    // Aguarda o 1º fetch retornar antes de comparar — evita falso positivo
    // entre `data: undefined → 0` logo após login.
    if (total === undefined) return;

    let timer: ReturnType<typeof setTimeout> | undefined;
    const anterior = contagemAnteriorRef.current;

    if (anterior !== undefined && total > anterior) {
      toast.info('Você tem uma nova atividade pendente.');
      setNovasPendencias(true);
      timer = setTimeout(() => setNovasPendencias(false), 3000);
    }

    contagemAnteriorRef.current = total;

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [total]);

  return (
    <Link
      to="/pendencias"
      className="relative inline-flex items-center justify-center h-9 w-9 rounded-md hover:bg-accent"
      aria-label={`Pendências (${totalNorm})`}
      data-testid="badge-pendencias"
    >
      <Bell className="h-4 w-4" />
      {!isLoading && totalNorm > 0 ? (
        <Badge
          variant="destructive"
          className={cn(
            'absolute -top-1 -right-1 h-5 min-w-[1.25rem] px-1 text-[10px]',
            novasPendencias && 'animate-pulse',
          )}
          data-testid="badge-pendencias-contador"
        >
          {totalNorm > 99 ? '99+' : totalNorm}
        </Badge>
      ) : null}
    </Link>
  );
}
